import { sha256 } from 'js-sha256'
import { useAppMode } from '~/composables/app'
import type { Cart, Gender, UserResponse } from '~/types/api/data-contracts'
import type { AppTrackingTransparencyStatus, CustomData, GrilldEventsRequest } from '~/types/api/events-data-contracts'
import { track } from './api'
import { useTrackStore } from '~/store'
import { Device, type DeviceInfo } from '@capacitor/device'
import { App, type AppInfo } from '@capacitor/app'
import { AdvertisingId } from '@capacitor-community/advertising-id'
import Cookies from 'js-cookie'
import type { UserDetails } from '~/types/user'
import { createClientTrackingEventId, trackCanonicalEvent } from './canonicalTracking'

export function useTracking() {
  const appMode = useAppMode()
  const trackStore = useTrackStore()
  async function identifyApp() {
    try {
      // Only track app details if the app is running in a mobile environment
      if (!isPlatform('capacitor')) {
        return
      }

      const lastUpdated = trackStore.app?.lastUpdated
      // if the device details were updated in the last hour, don't update it again
      if (lastUpdated && Date.now() - lastUpdated < 1000 * 60 * 60) {
        return
      }

      const appInfo: AppInfo = await App.getInfo()

      trackStore.updateApp({
        appName: appInfo.name,
        packageName: appInfo.id,
        shortVersion: appInfo.version,
        longVersion: `${appInfo.version} ${appInfo.build}`,
        lastUpdated: Date.now()
      })
    } catch (error) {
      console.warn(error)
    }
  }

  /** To be called on app start or resume to initialise the tracking details for the
   *  app and device
   */
  async function initialiseTrackingAppDetails() {
    if (appMode.isMobileMode) {
      // Mobile tracking
      identifyDevice()
      await identifyApp()
      await identifyAdvertisingIdentifiers()
    }
  }

  async function identifyAdvertisingIdentifiers() {
    // Only track advertising identifiers if the app is running in a mobile environment
    if (!isPlatform('capacitor')) {
      return
    }
    try {
      const getStatusResult = await AdvertisingId.getAdvertisingStatus()
      let status = getStatusResult.status
      let id: string | undefined = undefined
      if (status !== 'Authorized') {
        // Do not request tracking on iOS to avoid the ATT prompt
        // This may be revisited when the business decides to prompt for Ad tracking
        if (!isPlatform('ios')) {
          const requestResult = await AdvertisingId.requestTracking()
          status = requestResult.value
        }
      }

      if (status === 'Authorized') {
        const idResult = await AdvertisingId.getAdvertisingId()
        id = idResult.id
        status = idResult.status
      }
      trackStore.updateAdvertisingIdentifiers({
        attAuthorizationStatus: getAppTrackingTransparencyStatus(status),
        advertiserId: id,
        lastUpdated: Date.now(),
        ...(id && { advertiserId: id })
      })
    } catch (error) {
      console.error('Error:', error)
    }
  }

  function getAppTrackingTransparencyStatus(status: string): AppTrackingTransparencyStatus {
    switch (status) {
      case 'Authorized':
        return 'Authorized'
      case 'Denied':
        return 'Denied'
      case 'NotDetermined':
        return 'NotDetermined'
      case 'Restricted':
        return 'Restricted'
      default:
        return 'NotApplicable'
    }
  }

  async function identifyDevice() {
    try {
      // Only track device details if the app is running in a mobile environment
      if (!isPlatform('capacitor')) {
        return
      }
      const lastUpdated = trackStore.device?.lastUpdated
      // if the device details were updated in the last hour, don't update it again
      if (lastUpdated && Date.now() - lastUpdated < 1000 * 60 * 60) {
        return
      }

      const id = await Device.getId()
      const deviceInfo: DeviceInfo = await Device.getInfo()
      const locale = await Device.getLanguageTag()

      const deviceTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

      trackStore.updateDevice({
        vendorId: id.identifier,
        mobilePlatform: deviceInfo.platform === 'ios' ? 'Ios' : 'Android',
        osVersion: deviceInfo.osVersion,
        deviceModelName: deviceInfo.model,
        deviceTimeZone: deviceTimeZone,
        locale: locale.value,
        lastUpdated: Date.now()
      })
    } catch (error) {
      console.warn(error)
    }
  }

  function identifyBrowser() {
    try {
      // Only track browser details if the app is running in a web environment
      if (!appMode.isWebMode) {
        return
      }
      // Get click id from the meta cookie, if available
      const fbc = Cookies.get('_fbc')
      const fbp = Cookies.get('_fbp')
      const ttc = Cookies.get('_ttc')
      const ttp = Cookies.get('_ttp')

      if (fbc) {
        trackStore.browser = {
          ...trackStore.browser,
          fbc: fbc,
          metaClickId: fbc.split('.')[2]
        }
      }
      if (fbp) {
        trackStore.browser = {
          ...trackStore.browser,
          fbp: fbp
        }
      }
      if (ttc) {
        trackStore.browser = {
          ...trackStore.browser,
          ttc: ttc
        }
      }
      if (ttp) {
        trackStore.browser = {
          ...trackStore.browser,
          ttp: ttp
        }
      }
    } catch (error) {
      console.warn(error)
    }
  }

  function identifyUser(user: UserResponse | UserDetails | undefined) {
    // set up hashed user parameters or clear the existing user tracking when signed out
    function hash(val: string): string {
      const hash = sha256.create()
      hash.update(val)
      return hash.toString()
    }

    function toGenderString(gender?: Gender): string | undefined {
      // covert gender to 'm' or 'f' for gender tracking string
      switch (gender) {
        case 1:
          return 'f'
        case 2:
          return 'm'
        default:
          return undefined
      }
    }

    function transformDateOfBirth(dateOfBirth?: string | null): string | undefined {
      // transform the date of birth from dd/mm/yyyy to yyyymmdd
      if (dateOfBirth) {
        const [day, month, year] = dateOfBirth.split('/')
        return `${year}${month}${day}`
      }
    }

    function transformMetaPhone(phone?: string | null): string | undefined {
      if (phone) {
        let cleaned = phone.replace(/\D/g, '')
        // remove any leading zeros
        cleaned = cleaned.replace(/^0+/, '')
        // start with the country code (61 for AU)
        return `61${cleaned}`
      }
    }

    function transformTikTokPhone(phone?: string | null): string | undefined {
      if (phone) {
        // remove symbols, letters
        let cleaned = phone.replace(/\D/g, '')
        // remove any leading zeros
        cleaned = cleaned.replace(/^0+/, '')
        // start with the country code (61 for AU)
        return `+61${cleaned}`
      }
    }

    function transformSnapchatPhone(phone?: string | null): string | undefined {
      // Only digits with country code, area code and number. No other formatting characters
      if (phone) {
        // remove symbols, letters
        let cleaned = phone.replace(/\D/g, '')
        // remove any leading zeros
        cleaned = cleaned.replace(/^0+/, '')
        // start with the country code (61 for AU)
        return `61${cleaned}`
      }
    }

    function transformRedditPhone(phone?: string | null): string | undefined {
      if (phone) {
        // remove symbols, letters
        let cleaned = phone.replace(/\D/g, '')
        // remove any leading zeros
        cleaned = cleaned.replace(/^0+/, '')
        // start with the country code (61 for AU)
        return `+61${cleaned}`
      }
    }

    function transformRedditEmail(email?: string | null): string | undefined {
      if (email) {
        // remove leading and trailing spaces and convert to lowercase
        email = email.trim().toLowerCase()
        // split local and domain parts
        const parts = email.split('@')
        let localPart = parts[0]
        const domainPart = parts[1]

        // remove anything after '+'
        if (localPart.includes('+')) {
          localPart = localPart.split('+')[0]
        }

        return localPart + '@' + domainPart
      }
    }

    try {
      trackStore.updateUser({
        externalId: user && 'id' in user && user.id !== null ? user.id : undefined,
        email: user?.emailAddress ?? undefined,
        phone: user?.mobileNumber ?? undefined,
        firstName: user?.givenName ?? undefined,
        lastName: user?.familyName ?? undefined,
        dateOfBirth: user && 'dateOfBirth' in user ? transformDateOfBirth(user.dateOfBirth) : undefined, // format as yyyymmdd
        gender: user && 'gender' in user ? toGenderString(user.gender) : undefined,
        countryCode: 'au' // assume AU while grill'd app supports AU restaurants only
      })

      const params: UserIdentificationParams = {
        email: user?.emailAddress ?? undefined,
        phone: user?.mobileNumber ?? undefined,
        externalId: user && 'id' in user && user.id !== null ? user.id : undefined
      }

      // When hashing user details for Meta requires:
      // Phone - remove Remove symbols, letters, and any leading zeros.
      //       - start with the country code (61 for AU)
      // Email - Remove leading and trailing spaces, and convert all characters to lowercase.

      const metaTransformedPhone = params.phone ? transformMetaPhone(params.phone) : undefined
      const metaHashedPhone = metaTransformedPhone ? hash(metaTransformedPhone) : undefined

      // When hashing user details for TikTok requires:
      // Phone - remove Remove symbols, letters, and any leading zeros.
      //       - start with the country code and + symbol (+61 for AU)
      // Email - Remove leading and trailing spaces, and convert all characters to lowercase.

      const tikTokTransformedPhone = params.phone ? transformTikTokPhone(params.phone) : undefined
      const tikTokHashedPhoneNumber = tikTokTransformedPhone ? hash(tikTokTransformedPhone) : undefined

      const snapTransformedPhone = params.phone ? transformSnapchatPhone(params.phone) : undefined
      const snapHashedPhone = snapTransformedPhone ? hash(snapTransformedPhone) : undefined

      const redditTransformedPhone = params.phone ? transformRedditPhone(params.phone) : undefined
      const redditHashedPhone = redditTransformedPhone ? hash(redditTransformedPhone) : undefined
      const redditTransformedEmail = params.email ? transformRedditEmail(params.email) : undefined
      const redditHashedEmail = redditTransformedEmail ? hash(redditTransformedEmail) : undefined

      const hashedParams = {
        email: params.email ? hash(params.email.toLowerCase()) : undefined,
        phone_number: tikTokHashedPhoneNumber,
        external_id: params.externalId ? hash(params.externalId) : undefined
      }

      if (isMetaEnabled()) {
        const config = useRuntimeConfig()

        // https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching/
        const metaParams = {
          em: hashedParams.email,
          ph: metaHashedPhone,
          external_id: hashedParams.external_id
        }
        window.fbq('init', config.public.metaPixelId, metaParams)
      }
      if (isTikTokEnabled()) {
        window.ttq.identify(hashedParams)
      }
      if (isSnapchatEnabled()) {
        const snapchatParams: any = {
          user_hashed_email: hashedParams.email,
          user_hashed_phone_number: snapHashedPhone
        }
        window.snaptr('init', import.meta.env.VITE_SNAPCHAT_PIXEL_ID, snapchatParams)
      }
      if (isRedditEnabled()) {
        const redditParams: any = {
          email: redditHashedEmail,
          phoneNumber: redditHashedPhone,
          externalId: hashedParams.external_id
        }
        window.rdt('init', import.meta.env.VITE_REDDIT_PIXEL_ID, redditParams)
      }
    } catch (error) {
      console.warn(error)
    }
  }

  async function sendTrackingEvents(
    eventName: string,
    customData: CustomData,
    eventId: string,
    eventNameTikTok?: string
  ): Promise<void> {
    // if the tracking store has not been initialised, return without sending request
    // this may happen when triggered from initial page load
    if ((appMode.isMobileMode && !trackStore.device && !trackStore.app) || (appMode.isWebMode && !trackStore.browser)) {
      console.warn('Store not initialised')
      return
    }

    const body: GrilldEventsRequest = {
      eventId,
      data: {
        eventName: eventName,
        eventSource: appMode.isMobileMode ? 'App' : 'Web',
        eventTime: 0, // will be set server-side
        user: {
          email: trackStore.user?.email,
          phone: trackStore.user?.phone,
          externalId: trackStore.user?.externalId,
          firstName: trackStore.user?.firstName,
          lastName: trackStore.user?.lastName,
          dateOfBirth: trackStore.user?.dateOfBirth,
          gender: trackStore.user?.gender,
          city: trackStore.user?.city,
          state: trackStore.user?.state,
          zip: trackStore.user?.zip,
          countryCode: trackStore.user?.countryCode,
          locale: trackStore.device?.locale,
          metaClickId: trackStore.browser?.fbc,
          metaBrowserId: trackStore.browser?.fbp,
          tikTokClickId: trackStore.browser?.ttc,
          tikTokCookieId: trackStore.browser?.ttp
        },
        customData,
        ...(appMode.isMobileMode
          ? {
              app: {
                mobilePlatform: trackStore.device?.mobilePlatform ?? (isPlatform('ios') ? 'Ios' : 'Android'),
                appName: trackStore.app?.appName ?? "Grill'd",
                mobileAdvertiserIdIos: isPlatform('ios') ? trackStore.advertisingIdentifiers?.advertiserId : undefined,
                mobileAdvertiserVendorId: trackStore.device?.vendorId,
                mobileAdvertiserIdGoogle: isPlatform('android')
                  ? trackStore.advertisingIdentifiers?.advertiserId
                  : undefined,
                attAuthorizationStatus: isPlatform('ios')
                  ? (trackStore.advertisingIdentifiers?.attAuthorizationStatus ?? 'NotDetermined')
                  : 'NotApplicable',
                // Is app tracking enabled. Defaults to true if not set
                applicationTrackingEnabled: trackStore.advertisingIdentifiers?.applicationTrackingEnabled ?? true,
                appPackageName: trackStore.app?.packageName,
                shortVersion: trackStore.app?.shortVersion,
                longVersion: trackStore.app?.longVersion,
                osVersion: trackStore.device?.osVersion,
                deviceModelName: trackStore.device?.deviceModelName,
                timeZoneAbbreviation: trackStore.device?.timeZoneAbbreviation,
                carrier: trackStore.device?.carrier,
                screenWidth: trackStore.device?.screenWidth,
                screenHeight: trackStore.device?.screenHeight,
                screenDensity: trackStore.device?.screenDensity,
                // While additional details such as external storage size may be tracked by facebook, it
                // us explicitly not allowed by iOS to be sent off-device, so is not included here
                deviceTimeZone: trackStore.device?.deviceTimeZone
              }
            }
          : {
              page: {
                url: window.location.href,
                referrer: document.referrer
              }
            }),
        ...(eventNameTikTok ? { eventNameAlternates: { tiktok: eventNameTikTok } } : undefined)
      }
    }
    await track(body)
  }

  function trackMetaEvent(eventName: string, params: any, eventId?: string) {
    if (isMetaEnabled()) {
      window.fbq('track', eventName, params, { ...(eventId ? { eventID: eventId } : {}) })
    }
  }

  function trackMetaCustomEvent(eventName: string, params: any, eventId?: string) {
    if (isMetaEnabled()) {
      window.fbq('trackCustom', eventName, params, { ...(eventId ? { eventID: eventId } : {}) })
    }
  }
  function trackTikTokEvent(eventName: string, params: any, eventId?: string) {
    if (isTikTokEnabled()) {
      window.ttq.track(eventName, params, { ...(eventId ? { eventID: eventId } : {}) })
    }
  }
  function trackSnapchatEvent(eventType: string, params: any, eventId?: string) {
    if (isSnapchatEnabled()) {
      window.snaptr('track', eventType, params, { ...(eventId ? { client_deduplication_id: eventId } : {}) })
    }
  }
  function trackRedditEvent(eventType: string, params: any, eventId?: string) {
    if (isRedditEnabled()) {
      window.rdt('track', eventType, { ...params, ...(eventId ? { conversionId: eventId } : {}) })
    }
  }
  function trackRedditCustomEvent(eventName: string, params: any, eventId?: string) {
    if (isRedditEnabled()) {
      window.rdt('track', 'Custom', {
        customEventName: eventName,
        ...params,
        ...(eventId ? { conversionId: eventId } : {})
      })
    }
  }
  function trackTikTokPage(eventId?: string) {
    if (isTikTokEnabled()) {
      window.ttq.page({}, { ...(eventId ? { eventID: eventId } : {}) })
    }
  }
  function trackSnapChatPage(eventId?: string) {
    if (isSnapchatEnabled()) {
      window.snaptr('track', 'PAGE_VIEW', { ...(eventId ? { client_deduplication_id: eventId } : {}) })
    }
  }
  function trackRedditPage(eventId?: string) {
    if (isRedditEnabled()) {
      window.rdt('track', 'PageVisit', { ...(eventId ? { conversionId: eventId } : {}) })
    }
  }
  return {
    initialiseTrackingAppDetails,
    identifyApp,
    identifyBrowser,
    identifyAdvertisingIdentifiers,
    identifyDevice,
    identifyUser,
    sendTrackingEvents,
    trackMetaEvent,
    trackMetaCustomEvent,
    trackSnapchatEvent,
    trackTikTokEvent,
    trackTikTokPage,
    trackSnapChatPage,
    trackRedditEvent,
    trackRedditCustomEvent,
    trackRedditPage
  }
}

function isMetaEnabled() {
  // Conditionally enable meta tracking based on app mode
  // Currently enabled for both web and mobile
  const appMode = useAppMode()

  return appMode.isWebMode || appMode.isMobileMode
}

function isTikTokEnabled() {
  // Conditionally enable tiktok tracking based on app mode
  // Currently enabled for both web and mobile
  const appMode = useAppMode()

  return appMode.isWebMode || appMode.isMobileMode
}

function isSnapchatEnabled() {
  // Conditionally enable snapchat tracking based on app mode
  // Currently enabled for both web and mobile
  const appMode = useAppMode()

  return appMode.isWebMode || appMode.isMobileMode
}

function isRedditEnabled() {
  // Conditionally enable reddit tracking based on app mode
  // Currently enabled for both web and mobile
  const appMode = useAppMode()

  return appMode.isWebMode || appMode.isMobileMode
}

export function trackingEventViewPage(params: ViewPageParams) {
  const eventId = createClientTrackingEventId('page_view')

  // Page navigation does not have a matching explicit event in analytics.ts.
  // Emit it here so embedded and native navigation reaches sGTM as well as the
  // legacy Events API and browser pixels below.
  trackCanonicalEvent('page_view', { page_path: params.content_name }, eventId)

  const customData: CustomData = {
    contentName: params.content_name
  }

  const tracking = useTracking()
  tracking
    .sendTrackingEvents('ViewContent', customData, eventId, 'Pageview')
    .then(() => {
      tracking.trackMetaEvent('ViewContent', params, eventId)
      tracking.trackTikTokPage(eventId)
      tracking.trackSnapChatPage(eventId)
      tracking.trackRedditPage(eventId)
    })
    .catch((error) => {
      console.error('Error sending tracking events: ViewContent', error)
      // The server may have accepted the request before the client observed the
      // failure, so preserve the same ID for any browser-side fallback.
      tracking.trackMetaEvent('ViewContent', params, eventId)
      tracking.trackTikTokPage(eventId)
      tracking.trackSnapChatPage(eventId)
      tracking.trackRedditPage(eventId)
    })
}

export function trackingEventOnUserSuccessfullySignedUp(params: CompleteRegistrationTrackingParams, eventId: string) {
  const customData: CustomData = {
    currency: params.currency,
    value: params.value?.toString(),
    contentName: params.content_name,
    status: params.status ? 'true' : 'false'
  }

  const tracking = useTracking()
  tracking
    .sendTrackingEvents('CompleteRegistration', customData, eventId)
    .then(() => {
      tracking.trackMetaEvent('CompleteRegistration', params, eventId)
      tracking.trackTikTokEvent('CompleteRegistration', params, eventId)
      tracking.trackSnapchatEvent('SIGN_UP', params, eventId)
      tracking.trackRedditEvent('SignUp', params, eventId)
    })
    .catch((error) => {
      console.error('Error sending tracking events: CompleteRegistration', error)
    })
}

export function trackingEventOnUserSignIn(params: SignInTrackingParams, eventId: string) {
  // No standard meta pixel event for sign in. Must use custom event.
  const customData: CustomData = {
    contentName: params.content_name,
    status: params.status ? 'true' : 'false'
  }

  const tracking = useTracking()
  tracking
    .sendTrackingEvents('user_sign_in', customData, eventId, 'login')
    .then(() => {
      tracking.trackMetaCustomEvent('user_sign_in', params, eventId)
      tracking.trackTikTokEvent('login', params, eventId)
      tracking.trackSnapchatEvent('LOGIN', params, eventId)
      // No standard reddit event for sign in. Must use custom event.
      tracking.trackRedditCustomEvent('Login', params, eventId)
    })
    .catch((error) => {
      console.error('Error sending tracking events: user_sign_in', error)
    })
}

export function trackingEventBeginCheckout(params: BeginCheckoutTrackingParams, eventId: string) {
  const customData: CustomData = {
    currency: params.currency,
    value: params.value?.toString(),
    contentCategory: params.content_category,
    contentIds: params.content_ids,
    contents: params.contents?.map((content) => ({
      id: content.id,
      quantity: content.quantity?.toString(),
      itemPrice: content.itemPrice?.toString()
    })),
    numItems: params.num_items?.toString()
  }

  const tracking = useTracking()
  tracking
    .sendTrackingEvents('InitiateCheckout', customData, eventId)
    .then(() => {
      tracking.trackMetaEvent('InitiateCheckout', params, eventId)
      tracking.trackTikTokEvent('InitiateCheckout', params, eventId)
      tracking.trackSnapchatEvent(
        'START_CHECKOUT',
        {
          'price': params.value,
          'currency': params.currency,
          'item_ids': params.content_ids,
          'number_items': params.num_items
        },
        eventId
      )
      tracking.trackRedditCustomEvent('InitiateCheckout', params, eventId)
    })
    .catch((error) => {
      console.error('Error sending tracking events: InitiateCheckout', error)
    })
}

export function trackingEventPurchase(params: PurchaseEventTrackingParams, eventId: string) {
  const customData: CustomData = {
    currency: params.currency,
    value: params.value?.toString(),
    contentName: params.content_name,
    contentType: params.content_type,
    contentIds: params.content_ids,
    contents: params.contents?.map((content) => ({
      id: content.id,
      quantity: content.quantity?.toString(),
      itemPrice: content.itemPrice?.toString()
    })),
    numItems: params.num_items?.toString(),
    orderId: params.orderId
  }

  const tracking = useTracking()
  tracking
    .sendTrackingEvents('Purchase', customData, eventId, 'CompletePayment')
    .then(() => {
      tracking.trackMetaEvent('Purchase', params, eventId)
      const totalQuantity = params.contents
        ? params.contents.reduce((total, content) => total + content.quantity, 0)
        : 0
      const contents = params.contents
        ? params.contents.map((content) => ({
            content_id: content.id,
            quantity: content.quantity,
            price: content.itemPrice
          }))
        : []
      tracking.trackTikTokEvent(
        'CompletePayment',
        {
          content_type: params.content_type,
          quantity: totalQuantity,
          currency: params.currency,
          value: params.value,
          description: params.content_name,
          contents: contents
        },
        eventId
      )
      tracking.trackSnapchatEvent(
        'PURCHASE',
        {
          'price': params.value,
          'currency': params.currency,
          'item_ids': params.content_ids,
          'number_items': params.num_items
        },
        eventId
      )
      tracking.trackRedditEvent(
        'Purchase',
        {
          products: params.contents.map((content) => ({
            id: content.id
          })),
          currency: params.currency,
          value: params.value,
          item_count: params.contents.reduce((total, content) => total + content.quantity, 0)
        },
        eventId
      )
    })
    .catch((error) => {
      console.error('Error sending tracking events: Purchase', error)
    })
}

export function trackingEventViewItem(params: ViewContentTrackingParams, eventId: string) {
  const customData: CustomData = {
    currency: params.currency,
    value: params.value?.toString(),
    contentName: params.content_name,
    contentType: params.content_type,
    contentIds: params.content_ids,
    contents: params.contents?.map((content) => ({
      id: content.id,
      quantity: content.quantity?.toString(),
      itemPrice: content.itemPrice?.toString()
    })),
    numItems: params.num_items?.toString()
  }

  const tracking = useTracking()
  tracking
    .sendTrackingEvents('ViewContent', customData, eventId)
    .then(() => {
      tracking.trackMetaEvent('ViewContent', params, eventId)

      tracking.trackTikTokEvent(
        'ViewContent',
        {
          content_type: params.content_type,
          quantity: params.num_items,
          currency: params.currency,
          value: params.value,
          description: params.content_name,
          content_id: params.content_ids[0]
        },
        eventId
      )
      tracking.trackSnapchatEvent(
        'VIEW_CONTENT',
        {
          'price': params.value,
          'currency': params.currency,
          'item_ids': params.content_ids,
          'number_items': params.num_items
        },
        eventId
      )
      tracking.trackRedditEvent(
        'ViewContent',
        {
          products: params.contents.map((content) => ({
            id: content.id,
            name: params.content_name
          }))
        },
        eventId
      )
    })
    .catch((error) => {
      console.error('Error sending tracking events: ViewItem', error)
    })
}

export function trackingEventAddToCart(params: AddToCartEventTrackingParams, eventId: string) {
  const customData: CustomData = {
    currency: params.currency,
    value: params.value?.toString(),
    contentIds: params.content_ids,
    contentType: params.content_type,
    contents: params.contents?.map((content) => ({
      id: content.id,
      quantity: content.quantity?.toString(),
      itemPrice: content.itemPrice?.toString()
    }))
  }

  const tracking = useTracking()
  tracking
    .sendTrackingEvents('AddToCart', customData, eventId)
    .then(() => {
      tracking.trackMetaEvent('AddToCart', params, eventId)
      tracking.trackTikTokEvent(
        'AddToCart',
        {
          content_type: params.content_type,
          quantity: params.contents[0].quantity,
          currency: params.currency,
          value: params.value,
          description: params.content_name,
          content_id: params.content_ids[0]
        },
        eventId
      )
      tracking.trackSnapchatEvent(
        'ADD_CART',
        {
          price: params.value,
          currency: params.currency,
          item_ids: params.content_ids,
          number_items: params.contents.reduce((total, content) => total + content.quantity, 0)
        },
        eventId
      )
      tracking.trackRedditEvent(
        'AddToCart',
        {
          products: params.contents.map((content) => ({
            id: content.id,
            name: params.content_name
          })),
          currency: params.currency,
          value: params.value,
          item_count: params.contents.reduce((total, content) => total + content.quantity, 0)
        },
        eventId
      )
    })
    .catch((error) => {
      console.error('Error sending tracking events: AddToCart', error)
    })
}

export function getTrackingContentIds(cart: Cart | undefined): string[] {
  return cart!.items ? cart!.items.map((item) => String(item.id)) : []
}

export function getTrackingContents(cart: Cart | undefined): TrackingContentObject[] {
  return cart!.items
    ? cart!.items.map(
        (item) =>
          ({
            id: String(item.id),
            quantity: item.quantity,
            itemPrice: item.unitPriceInCents ? item.unitPriceInCents / 100 : 0
          }) as TrackingContentObject
      )
    : []
}

export interface UserIdentificationParams {
  email?: string // The email of the user
  phone?: string // The phone number
  externalId?: string // The external ID of the user
}

export interface CompleteRegistrationTrackingParams {
  content_name?: string // Name of the page / product
  currency?: string // The value for the value specified
  status?: boolean // Used with the CompleteRegistration event to show the status of the registration
  value?: number // The value of a user performing this event to the business
}

export interface SignInTrackingParams {
  content_name?: string // Name of the page / product
  status?: boolean // Used with the CompleteRegistration event to show the status of the registration
}

export interface TrackingContentObject {
  id: string // The ID of the product
  quantity: number // The quantity of the product
  itemPrice: number // The price of the product
}

export interface BeginCheckoutTrackingParams {
  content_ids: string[] // Array of product IDs
  content_category?: string // Category of the page/product.
  contents?: TrackingContentObject[] // An array of JSON objects that contains the quantity and the International Article Number (EAN) when applicable, or other product or content identifier(s)
  num_items?: number // Number of items in the cart
  currency?: string // Required the value for the value specified
  value?: number // Required the value of a user performing this event to the business
}

export interface PurchaseEventTrackingParams {
  content_ids: string[] // Array of product IDs
  content_name?: string // Name of the page / product
  content_type?: string // Type of content product or product_group
  contents: TrackingContentObject[] // An array of JSON objects that contains the quantity and the International Article Number (EAN) when applicable, or other product or content identifier(s)
  num_items?: number // Number of items in the cart
  currency: string // Required the value for the value specified
  value: number // Required the value of a user performing this event to the business
  orderId?: string // The order ID
}

export interface AddToCartEventTrackingParams {
  content_ids: string[] // Array of product IDs
  content_name?: string // Name of the page / product
  content_type?: string // Type of content product or product_group
  contents: TrackingContentObject[] // An array of JSON objects that contains the quantity and the International Article Number (EAN) when applicable, or other product or content identifier(s)
  currency: string // Required the value for the value specified
  value: number // Required the value of a user performing this event to the business
}

export interface ViewContentTrackingParams {
  content_ids: string[] // Array of product IDs
  content_category?: string // Category of the page/product.
  content_name?: string // Name of the page / product
  content_type?: string // Type of content product or product_group
  contents: TrackingContentObject[] // An array of JSON objects that contains the quantity and the International Article Number (EAN) when applicable, or other product or content identifier(s)
  num_items?: number // Number of items in the cart
  currency: string // Required the value for the value specified
  value: number // Required the value of a user performing this event to the business
}

export interface ViewPageParams {
  content_name?: string // Name of the page / product
}
